import { inject, Injectable } from '@angular/core';
import { AuthService } from './auth.service';

type Action = 'add' | 'edit' | 'delete' | 'export';
type ModuleAccess = Record<Action, boolean>;

const ROUTE_APIKEY: Record<string, string> = {
  '/games': 'Games',
  '/categories': 'Categories',
  '/challenges': 'Challenges & Events',
  '/rewards': 'Rewards',
  '/leaderboards': 'Leaderboards',
  '/players': 'Players',
  '/admins': 'Admin Users',
  '/claims': 'Prize Claims',
  '/moderation': 'Moderation',
  '/support': 'Support & Notifications',
  '/promotion/home-banners': 'Ad Banners',
  '/promotion/ad-banners': 'Ad Banners',
  '/promotion/push-notifications': 'Ad Banners',
  '/settings/language': 'Settings',
  '/settings/cms-pages': 'Settings',
  '/settings/faqs': 'Settings',
  '/settings/email': 'Settings',
  '/settings/global': 'Settings',
  '/settings/badges': 'Settings',
  '/settings/collections': 'Settings',
  '/settings/achievements': 'Settings',
};

@Injectable({ providedIn: 'root' })
export class PermissionService {
  private auth = inject(AuthService);

  isSubAdmin(): boolean {
    return this.auth.getAdmin()?.adminType === 'Sub Admin';
  }

  private moduleAccess(apiKey: string): ModuleAccess | null {
    const access = this.auth.getAdmin()?.access as Record<string, Partial<ModuleAccess>> | undefined;
    const entry = access?.[apiKey];
    if (!entry) return null;
    return { add: !!entry.add, edit: !!entry.edit, delete: !!entry.delete, export: !!entry.export };
  }

  canAccessRoute(path: string): boolean {
    if (!this.isSubAdmin()) return true;
    if (path === '/dashboard' || path === '') return true;

    const apiKey = ROUTE_APIKEY[path];
    if (!apiKey) return true;

    const m = this.moduleAccess(apiKey);
    return !!m && (m.add || m.edit || m.delete || m.export);
  }

  can(apiKey: string, action: Action): boolean {
    if (!this.isSubAdmin()) return true;
    return !!this.moduleAccess(apiKey)?.[action];
  }
}
